import React, { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryImageUrl } from "@/utils/getCategoryImageUrl";
import { Project } from "@/types";
import { CategoryType } from "@/types/category";
import { Card, CardContent } from "@/components/ui/card";
import { useCategoryData } from "@/hooks/useCategoryData";
import { Button } from "../ui/button";

interface CategoryCardsProps {
  projects: Project[];
  projectToBatchIds: Record<string, string[]>;
  batchTitles: Record<string, string>;
  selectedCategory?: CategoryType;
  selectedProjectId?: string;
  customerColor: string;
  onCategoryClick?: (category: CategoryType) => void;
}

const CategoryCards: React.FC<CategoryCardsProps> = ({
  projects,
  projectToBatchIds,
  batchTitles,
  selectedCategory,
  selectedProjectId,
  customerColor,
  onCategoryClick,
}) => {
  const { t } = useLanguage();
  const [visibleCategories, setVisibleCategories] = useState(6);

  // Get category data using the hook
  const { categoryData } = useCategoryData({
    projects,
    projectToBatchIds,
    batchTitles,
    selectedCategory,
    selectedProjectId,
  });

  // Handle show more button click
  const handleShowMore = () => {
    setVisibleCategories((prev) => prev + 6);
  };

  // Reset visible categories when filters change
  useEffect(() => {
    setVisibleCategories(6);
  }, [selectedCategory, selectedProjectId]);

  // Get visible categories
  const visibleCategoryData = categoryData.slice(0, visibleCategories);
  const hasMoreCategories = visibleCategories < categoryData.length;

  if (categoryData.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">{t("common.noData")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleCategoryData.map((category) => (
          <Card
            key={category.category}
            className="overflow-hidden hover:shadow-lg transition-shadow rounded-[20px] flex flex-col cursor-pointer"
            onClick={() => onCategoryClick?.(category.category)}
          >
            <div className="p-3 pb-5">
              <div
                className="h-40 bg-cover bg-center relative rounded-[12px]"
                style={{
                  backgroundImage: `url(${getCategoryImageUrl(
                    category.category
                  )})`,
                }}
              />
            </div>

            {/* Category color bar */}
            <div className="px-6">
              <div
                className="h-2 w-8 rounded-[9px]"
                style={{ backgroundColor: category.color }}
              />
            </div>

            <CardContent className="p-6 pt-2 flex flex-col flex-grow">
              <div className="flex-grow">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">
                  {category.displayName}
                </h3>

                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {category.description}
                </p>
              </div>

              <div className="flex items-center justify-start">
                <div className="bg-white text-black text-sm font-semibold border border-gray-200 rounded-xl px-4 py-2">
                  {category.totalMeasuresCount}{" "}
                  {category.totalMeasuresCount === 1
                    ? t("categoryOverview.measure")
                    : t("categoryOverview.measures")}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {hasMoreCategories && (
        <div className="text-center">
          <Button
            onClick={handleShowMore}
            className="mt-10 text-white rounded-lg transition-colors"
            style={{
              backgroundColor: customerColor,
            }}
          >
            {t("common.showMore")}
          </Button>
        </div>
      )}
    </>
  );
};

export default CategoryCards;
